import { clsx } from 'clsx'

import type { DraftAsset } from '~publish/components/Composer/assets/assetListOps'
import type { MediaSlide } from '~publish/components/Composer/assets/mediaRuns'
import {
  gifAsImage,
  toMediaRuns,
} from '~publish/components/Composer/assets/mediaRuns'
import type {
  Gif,
  Link,
} from '~publish/legacy/composer/composer/entities/factories'
import { AttachmentTypes } from '~publish/legacy/constants'
import { LinkifiedText } from '~publish/legacy/shared-components/LinkifiedText'
import { parseThreadsLinks } from '~publish/legacy/utils/channels/threads/helpers'

import { ThreadPostActions } from './ThreadPostActions'
import { ImagesCarousel } from '../../../Common/ImagesCarousel'
import { ImagesGrid } from '../../../Common/ImagesGrid'
import { LinkPreview } from '../../../Common/LinkPreview'
import useLinkPreviewEffect from '../../../Common/hooks/useLinkPreviewEffect'
import type { ProfilePreview, ThreadsContent } from '../../../types'
import { getScheduledTimeLabel } from '../../getScheduledTimeLabel'
import { AttachmentVideo } from '../AttachmentVideo'

import styles from './ThreadPost.module.css'

import type { JSX } from 'react'

interface GetMediaPropertiesResult {
  hasMedia: boolean
  hasImages: boolean | number | undefined | null | Gif
  hasVideo: boolean
  hasLink: boolean
  hasRetweet: boolean
}

const getMediaProperties = (
  content: ThreadsContent,
  linkPreview: Link | null,
): GetMediaPropertiesResult => {
  const { text, images, gif, video, enabledAttachmentType } = content

  const hasText = !!text
  const hasMedia = enabledAttachmentType === AttachmentTypes.MEDIA
  const hasImages = hasMedia && (images?.length || gif)
  const hasVideo = !!(hasMedia && video)
  const hasLink = !!(linkPreview && !hasImages && !hasVideo)
  const hasContent = hasText || hasMedia || hasLink
  // If there is no content, we display the whole tweet as a retweet.
  const hasRetweet =
    enabledAttachmentType === AttachmentTypes.RETWEET && hasContent

  return { hasMedia, hasImages, hasVideo, hasLink, hasRetweet }
}

export const ThreadPost = ({
  content,
  profile,
  index,
}: {
  content: ThreadsContent
  profile: ProfilePreview
  index: number
}): JSX.Element => {
  const {
    text,
    images,
    gif,
    video,
    videos,
    link: linkFromPost,
    inThread,
    channelData,
    scheduledAt,
  } = content
  const { avatar, username, displayName, timezone } = profile
  const [linkPreview] = useLinkPreviewEffect({
    linkFromPost,
    text,
    returnFirstUrl: true,
  })

  const { hasMedia, hasLink } = getMediaProperties(content, linkPreview)

  // `content.assets` is the ordered image/video/gif source of truth when the
  // caller provides it. Callers that haven't adopted it yet fall back to the
  // legacy images-array plus separate video field.
  const assets: readonly DraftAsset[] | null | undefined = content.assets
  const mediaSlides: MediaSlide[] = hasMedia
    ? assets?.length
      ? assets.reduce<MediaSlide[]>((acc, asset) => {
          if (asset.kind === 'image') {
            acc.push({ kind: 'image', image: asset.image })
          } else if (asset.kind === 'gif') {
            acc.push({ kind: 'image', image: gifAsImage(asset.gif) })
          } else if (asset.kind === 'video') {
            acc.push({ kind: 'video', video: asset.video })
          }
          return acc
        }, [])
      : [
          ...(images?.length
            ? images.map((image) => ({ kind: 'image' as const, image }))
            : gif
              ? [{ kind: 'image' as const, image: gifAsImage(gif) }]
              : []),
          ...(videos?.length
            ? videos.map((v) => ({ kind: 'video' as const, video: v }))
            : video
              ? [{ kind: 'video' as const, video }]
              : []),
        ]
    : []
  const mediaRuns = toMediaRuns(mediaSlides)

  const usernameToDisplay = displayName || username
  const topic = index === 0 ? channelData?.threads?.topic : null
  const location = index === 0 ? channelData?.threads?.location_name : null

  const scheduledTimeLabel = getScheduledTimeLabel(scheduledAt, timezone)

  const showBorder = !!inThread
  const showTwirl = !!text && (index + 1) % 3 === 0

  return (
    <>
      <div
        className={clsx(
          styles.wrapper,
          showBorder && styles.withBorder,
          showTwirl && styles.withTwirl,
        )}
        data-thread-id={index}
      >
        <div className={styles.avatarWrapper}>
          <img
            className={styles.avatar}
            alt={`${usernameToDisplay}'s avatar`}
            data-testid="avatar"
            src={avatar}
          />
        </div>
        <div className={styles.content}>
          <div className={styles.header}>
            <span className={styles.username} translate="no">
              {usernameToDisplay}
              {topic && (
                <>
                  <span className={styles.topicDivider}>&gt;</span>
                  <span>{topic}</span>
                </>
              )}
            </span>
            {scheduledTimeLabel && (
              <div className={styles.ago} data-testid="scheduled-time">
                {scheduledTimeLabel}
              </div>
            )}
          </div>

          {text ? (
            <LinkifiedText
              links={parseThreadsLinks(text, !!topic)}
              whitespace="pre-wrap"
              className={styles.text}
            >
              {text}
            </LinkifiedText>
          ) : (
            <div className={styles.emptyText} />
          )}

          {location && (
            <div className={styles.location} translate="no">
              {location}
            </div>
          )}

          {mediaRuns.map((run) =>
            run.kind === 'images' ? (
              run.images.length === 1 ? (
                <ImagesGrid
                  key={run.images.map((image) => image.url).join(',')}
                  images={run.images}
                  isRowFormat={false}
                  maxHeight="180px"
                  shouldShowGifTag={false}
                  className={styles.imagesGrid}
                />
              ) : (
                <ImagesCarousel
                  key={run.images.map((image) => image.url).join(',')}
                  images={run.images}
                  maxHeight="180px"
                  className={styles.imagesCarousel}
                />
              )
            ) : (
              <AttachmentVideo
                key={run.video.url}
                video={run.video}
                showSoundOffIcon
              />
            ),
          )}
          {hasLink && linkPreview && (
            <LinkPreview
              link={linkPreview}
              clickable={false}
              imageCover
              className={styles.linkPreview}
            />
          )}

          <ThreadPostActions />
        </div>
      </div>
    </>
  )
}
